/** Upcoming and past calls, meetings and comments of an account, with notes and transcripts. */
import { ListTodo, MessageSquare, Phone, Users } from "lucide-react";
import { useGetList, useNotify, useUpdate, type Identifier } from "ra-core";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import type { ZcrmActivity } from "../providers/zinspector/activities";
import type { ActivityKind } from "../providers/zinspector/crmConfig";
import { splitActivities } from "./activityUtils";

const ICONS: Record<ActivityKind, typeof Phone> = {
  Call: Phone,
  Meeting: Users,
  "To-do": ListTodo,
  Comment: MessageSquare,
};

const ALL = { page: 1, perPage: 500 };

export const ActivityList = ({
  companyId,
  contactNames,
}: {
  companyId: Identifier;
  contactNames: Map<string, string>;
}) => {
  const { data = [], isPending } = useGetList<ZcrmActivity>("activities", {
    filter: { company_id: companyId },
    pagination: ALL,
    sort: { field: "starts_at", order: "DESC" },
  });
  if (isPending) return null;
  const { upcoming, past } = splitActivities(data);

  return (
    <div className="flex flex-col gap-4">
      <Section title="Upcoming" empty="Nothing scheduled.">
        {upcoming.map((activity) => (
          <ActivityRow
            key={activity.id}
            activity={activity}
            contactName={contactNames.get(String(activity.contact_id))}
          />
        ))}
      </Section>
      <Section
        title="Past calls, meetings and comments"
        empty="Nothing logged yet. The first call you log or record shows here."
      >
        {past.map((activity) => (
          <ActivityRow
            key={activity.id}
            activity={activity}
            contactName={contactNames.get(String(activity.contact_id))}
          />
        ))}
      </Section>
    </div>
  );
};

const Section = ({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string;
  children: React.ReactNode[];
}) => (
  <Card>
    <CardContent className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold">{title}</h2>
      {children.length ? (
        <ul className="divide-y">{children}</ul>
      ) : (
        <p className="text-sm text-muted-foreground">{empty}</p>
      )}
    </CardContent>
  </Card>
);

const formatWhen = (date: string | null) =>
  date
    ? new Intl.DateTimeFormat("en", {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(new Date(date))
    : "";

const ActivityRow = ({
  activity,
  contactName,
}: {
  activity: ZcrmActivity;
  contactName?: string;
}) => {
  const Icon = ICONS[activity.kind];
  const [update, { isPending }] = useUpdate();
  const notify = useNotify();
  const markDone = () =>
    update(
      "activities",
      { id: activity.id, data: { is_done: true }, previousData: activity },
      {
        onSuccess: () =>
          notify(`${activity.kind} marked done`, { type: "success" }),
        onError: (error) =>
          notify(error instanceof Error ? error.message : "Update failed", {
            type: "error",
          }),
      },
    );

  return (
    <li className="flex flex-col gap-1.5 py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-2 min-w-0">
          <Icon
            className="size-4 mt-0.5 shrink-0 text-muted-foreground"
            aria-label={activity.kind}
          />
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-medium truncate">
              {activity.title}
            </span>
            <span className="text-xs text-muted-foreground">
              {[
                formatWhen(activity.starts_at),
                contactName ? `with ${contactName}` : "",
                activity.source,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </div>
        </div>
        {activity.is_done ? null : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={markDone}
            disabled={isPending}
          >
            Mark done
          </Button>
        )}
      </div>
      {activity.notes ? (
        <p className="text-sm whitespace-pre-line pl-6">{activity.notes}</p>
      ) : null}
      {activity.transcript ? (
        <details className="text-sm pl-6">
          <summary className="cursor-pointer text-muted-foreground">
            Transcript
          </summary>
          <pre className="mt-2 whitespace-pre-wrap font-sans text-xs bg-muted rounded-md p-3">
            {activity.transcript}
          </pre>
        </details>
      ) : null}
    </li>
  );
};
